import { useEffect, useState, type RefObject } from "react";
import { ArrowRight } from "lucide-react";

type Props = {
  triggerRef: RefObject<HTMLElement | null>;
  name: string;
  image: string;
  price: string;
  subtitle?: string;
  onAdd: () => void;
};

/**
 * Appears pinned to the bottom of the viewport when the user scrolls
 * past the provided trigger element (typically the buy box). Hides again
 * when scrolled back above it.
 */
export function StickyBuyBar({ triggerRef, name, image, price, subtitle, onAdd }: Props) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = triggerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        // Visible: trigger has scrolled out the top of the viewport
        const above = entry.boundingClientRect.bottom < 0;
        setShow(!entry.isIntersecting && above);
      },
      { threshold: 0, rootMargin: "0px 0px 0px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [triggerRef]);

  return (
    <div
      {...(!show && { inert: "" as unknown as boolean, "aria-hidden": true })}
      className={`pointer-events-none fixed inset-x-0 bottom-0 z-40 px-3 pb-3 transition-all duration-300 md:px-6 md:pb-5 ${
        show ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
      }`}
    >
      <div
        className={`pointer-events-auto mx-auto flex max-w-3xl items-center gap-3 rounded-2xl bg-background/85 p-2.5 backdrop-blur-xl md:gap-4 md:p-3 ${
          show ? "" : ""
        }`}
      >
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-muted ring-1 ring-foreground/5 md:h-14 md:w-14">
          <img src={image} alt={name} className="h-full w-full object-cover" loading="lazy" decoding="async" fetchPriority="low" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold leading-tight md:text-sm">{name}</p>
          <p className="mt-0.5 text-[11.5px] tabular-nums text-muted-foreground md:text-xs">
            <span className="font-bold text-foreground">{price}</span>
            {subtitle ? ` · ${subtitle}` : null}
          </p>
        </div>
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl bg-foreground px-4 py-2.5 text-[12.5px] font-bold text-background transition hover:opacity-90 md:px-5 md:text-sm"
        >
          Add to cart
          <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
        </button>
      </div>
    </div>
  );
}